/**
 * History conversion utilities for StudioAI feature
 */

import { ROLE_TYPES, STREAMING_EVENT_TYPES } from '../constants';
import type { ChatMessage, ChatMessageContent, StudioAIHistory } from '../types';
import { applyReplacementsToChartSpec, applyWordReplacements } from './text-utils';

// ============================================================================
// HISTORY CONVERSION UTILITIES
// ============================================================================

/**
 * Convert conversation history to chat messages
 */
export function convertHistoryToMessages(history: StudioAIHistory[]): ChatMessage[] {
    const messages: ChatMessage[] = [];

    history.forEach((historyItem) => {
        // Add user message
        messages.push({
            id: `${historyItem.id}_user`,
            role: ROLE_TYPES.USER,
            content: historyItem.userMessage,
            timestamp: new Date(historyItem.createdAt),
        });

        // Add AI response if available
        if (historyItem.aiResponse) {
            const content = convertAIResponseToContent(historyItem.aiResponse);

            messages.push({
                id: `${historyItem.id}_ai`,
                role: ROLE_TYPES.ASSISTANT,
                content,
                timestamp: new Date(historyItem.updatedAt),
                metadata: historyItem.responseMetadata,
                isStreaming: false, // Loaded messages should not use typewriter effect
            });
        }
    });

    return messages;
}

/**
 * Convert AI response to proper content format
 */
function convertAIResponseToContent(aiResponse: string | unknown[]): ChatMessageContent[] {
    if (typeof aiResponse === 'string') {
        // Simple string response - wrap in text content
        return [{ type: STREAMING_EVENT_TYPES.TEXT, text: applyWordReplacements(aiResponse) }];
    }

    if (Array.isArray(aiResponse)) {
        // Convert stored response structure to proper ChatMessageContent format
        return (aiResponse as unknown[]).map((item: unknown) => {
            const typedItem = item as Record<string, unknown>;

            // Handle different content types properly
            switch (typedItem.type) {
                case STREAMING_EVENT_TYPES.TEXT:
                    return {
                        type: STREAMING_EVENT_TYPES.TEXT,
                        text: applyWordReplacements(typedItem.text as string),
                    };

                case STREAMING_EVENT_TYPES.THINKING:
                    return {
                        type: STREAMING_EVENT_TYPES.THINKING,
                        text: applyWordReplacements(typedItem.text as string),
                    };

                case STREAMING_EVENT_TYPES.CHART: {
                    const chartData = typedItem.chart as {
                        chart_spec: string;
                        title?: string;
                    };
                    return {
                        type: STREAMING_EVENT_TYPES.CHART,
                        chart: {
                            chart_spec: applyReplacementsToChartSpec(chartData.chart_spec),
                            title: chartData.title
                                ? applyWordReplacements(chartData.title)
                                : undefined,
                        },
                    };
                }

                case STREAMING_EVENT_TYPES.TABLE:
                    return {
                        type: STREAMING_EVENT_TYPES.TABLE,
                        table: {
                            ...(typedItem.table as {
                                markdown: string;
                                isToolResult?: boolean;
                                rawData?: Record<string, unknown>;
                            }),
                            markdown: applyWordReplacements(
                                (
                                    typedItem.table as {
                                        markdown: string;
                                    }
                                ).markdown,
                            ),
                        },
                    };

                case STREAMING_EVENT_TYPES.FETCHED_TABLE:
                    // Legacy conversion
                    return {
                        type: STREAMING_EVENT_TYPES.TABLE,
                        table: {
                            markdown: applyWordReplacements(typedItem.tableMarkdown as string),
                            isToolResult: typedItem.toolResult as boolean,
                        },
                    };

                case STREAMING_EVENT_TYPES.TOOL_RESULT:
                    return {
                        type: STREAMING_EVENT_TYPES.TOOL_RESULT,
                        tool_result: typedItem.tool_result as {
                            name: string;
                            content: Array<{
                                type: string;
                                json?: Record<string, unknown>;
                            }>;
                        },
                    };

                case STREAMING_EVENT_TYPES.TOOL_USE:
                    return {
                        type: STREAMING_EVENT_TYPES.TOOL_USE,
                        tool_use: typedItem.tool_use as {
                            name: string;
                            input: Record<string, unknown>;
                        },
                    };

                case STREAMING_EVENT_TYPES.SQL_EXECUTING:
                    return {
                        type: STREAMING_EVENT_TYPES.SQL_EXECUTING,
                        sql_executing: typedItem.sql_executing as {
                            statement: string;
                            status: 'executing' | 'completed' | 'error';
                        },
                    };

                default:
                    // Fallback - try to preserve the structure as-is
                    console.warn('Unknown content type in conversation history:', typedItem.type);
                    return typedItem as unknown as ChatMessageContent;
            }
        });
    }

    // Fallback to string
    return [
        {
            type: STREAMING_EVENT_TYPES.TEXT,
            text: applyWordReplacements(String(aiResponse)),
        },
    ];
}
